import { NavLink, Link, useNavigate } from 'react-router-dom';
import {
  fanCognitoConfigured,
  getStoredFanClaims,
  signOutFan,
} from '../auth/fan-auth';
import { WalletPill } from './WalletPill';

// Single source of truth for nav link classes — keeps active styling
// consistent across links. `hidden sm:inline-flex` keeps the top-nav
// links off mobile (BottomNav owns mobile navigation); the top header
// on mobile is brand + login state only.
function navLinkClass({ isActive }: { isActive: boolean }): string {
  return [
    'hidden sm:inline-flex relative rounded-full px-2.5 py-1 transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-violet-500',
    // Active link gets a subtle violet text glow + a 2-px gradient
    // underline anchored to the bottom of the pill (via after:).
    // Mirrors the BottomNav neon-bar treatment for cross-surface
    // consistency without making the desktop pill look heavy.
    isActive
      ? 'bg-slate-800 text-violet-100 shadow-[inset_0_0_0_1px_rgba(167,139,250,0.25)] after:absolute after:inset-x-3 after:-bottom-0.5 after:h-[2px] after:rounded-full after:bg-gradient-to-r after:from-violet-500 after:via-fuchsia-400 after:to-blue-500 after:shadow-[0_0_8px_rgba(124,70,243,0.55)]'
      : 'text-slate-300 hover:text-white hover:bg-slate-800/60',
  ].join(' ');
}

export function AppHeader() {
  const navigate = useNavigate();
  const claims = getStoredFanClaims();
  const configured = fanCognitoConfigured();
  const signedIn = Boolean(claims?.sub);
  const name = claims?.name || claims?.email || '';

  return (
    <header
      className="border-b border-slate-800 bg-slate-900/70 backdrop-blur sticky top-0 z-20"
      // env(safe-area-inset-top) keeps the header below the iOS notch
      // when the SPA is launched in standalone PWA mode (apple-mobile-
      // web-app-status-bar-style=black-translucent). No-op when running
      // inside Safari chrome where the inset is 0.
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-30 focus:rounded focus:bg-violet-600 focus:px-3 focus:py-1 focus:text-white"
      >跳到主內容</a>
      <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-2">
        <Link
          to="/"
          className="flex items-center gap-2 text-slate-50 font-semibold tracking-wide focus:outline-none focus:ring-2 focus:ring-violet-500 rounded"
          aria-label="NOXOR 首頁"
          title="回首頁"
        >
          <img
            src="/logo_text_right_white.png"
            alt="NOXOR"
            className="h-[3.72rem] w-auto"
            // LCP candidate — header logo paints above the fold on every
            // signed-in fan SPA page. High-priority fetch shaves ms off
            // Largest Contentful Paint per Chrome's Priority Hints API.
            fetchPriority="high"
            decoding="async"
            onError={(e) => {
              const el = e.currentTarget as HTMLImageElement;
              if (el.src.endsWith('/logo_text_right_white.png')) {
                el.src = '/logo_text_right.png';
              } else if (el.src.endsWith('/logo_text_right.png')) {
                el.src = '/logo.png';
              } else {
                el.style.display = 'none';
              }
            }}
          />
        </Link>
        <nav aria-label="主要導覽" className="flex items-center gap-1 sm:gap-2 text-sm">
          {/* Nav links live in the BottomNav on mobile (<sm); these
              top-nav copies appear only on >= sm via the responsive
              `hidden sm:inline-flex` baked into navLinkClass. */}
          <NavLink to="/" end className={navLinkClass}>首頁</NavLink>
          {signedIn && (
            <NavLink to="/relationships" end className={navLinkClass}>養成</NavLink>
          )}
          <NavLink to="/objects" end className={navLinkClass}>物件庫</NavLink>
          {signedIn && (
            <>
              <NavLink to="/wallet" end className={navLinkClass}>錢包</NavLink>
              <NavLink to="/me" end className={navLinkClass}>個人</NavLink>
            </>
          )}
          {!configured ? (
            <span className="text-amber-400 text-xs ml-1">Fan Cognito 未設定</span>
          ) : signedIn ? (
            <div className="flex items-center gap-2 ml-1">
              {/* Always-visible platform-points stat. Self-fetches +
                  refreshes every 60s + on window focus. Tappable
                  shortcut to /wallet. */}
              <WalletPill />
              <span className="text-slate-400 text-xs hidden sm:inline" title={name}>{name}</span>
              <button
                type="button"
                onClick={() => { signOutFan(); navigate('/'); }}
                className="rounded-full border border-slate-700 px-3 py-1 text-slate-300 hover:bg-slate-800 active:scale-[0.97] transition focus:outline-none focus:ring-2 focus:ring-violet-500"
              >登出</button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="rounded-full bg-violet-500 px-3 py-1 text-white hover:bg-violet-400 active:scale-[0.97] transition focus:outline-none focus:ring-2 focus:ring-violet-300 ml-1 shadow-[0_4px_14px_-4px_rgba(124,70,243,0.6)]"
            >登入 / 註冊</button>
          )}
        </nav>
      </div>
    </header>
  );
}
